import React, { useState, useEffect, useRef } from "react";
import { X, Upload, Image as ImageIcon } from "lucide-react";

export default function EditConfigModal({ isOpen, onClose, data, onSave }) {
  const [value, setValue] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoBase64, setLogoBase64] = useState("");
  const [printLogo, setPrintLogo] = useState(true);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (data) {
      setValue(data.value);
      if (data.type === "image" && data.value) {
        setLogoPreview(data.value);
      }
      
      if (data.type === "image" && data.label && data.label.toLowerCase().includes("logo")) {
         try {
           const config = JSON.parse(localStorage.getItem("receiptPrintConfig") || "{}");
           setPrintLogo(config.printLogo !== false);
         } catch { }
      }
    }
  }, [data]);

  if (!isOpen || !data) return null;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("File harus berupa gambar");
        return;
      }

      // Validate file size (max 2MB)
      if (file.size > 2 * 1024 * 1024) {
        alert("Ukuran file maksimal 2MB");
        return;
      }

      setLogoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setLogoBase64(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
    setLogoBase64("");
    setValue("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // For logo, pass the base64 data
    if (data.type === "image") {
      if (data.label && data.label.toLowerCase().includes("logo")) {
         try {
           const config = JSON.parse(localStorage.getItem("receiptPrintConfig") || "{}");
           config.printLogo = printLogo;
           localStorage.setItem("receiptPrintConfig", JSON.stringify(config));
         } catch { }
      }
      onSave({ ...data, value: logoBase64 || value, isLogo: true });
    } else {
      onSave({ ...data, value });
    }
  };

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg w-full max-w-md flex flex-col modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between p-4 bg-teal-600 text-white rounded-t-lg">
          <h2 className="text-lg font-bold">Edit {data.label}</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-6">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {data.label}
              </label>
              {data.type === "textarea" ? (
                <textarea
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  rows={4}
                />
              ) : data.type === "image" ? (
                <div className="space-y-3">
                  {/* Upload Area */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-teal-500 transition">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                    {logoPreview ? (
                      <div className="space-y-3">
                        <img
                          src={logoPreview}
                          alt="Logo preview"
                          className="max-h-32 mx-auto rounded"
                        />
                        <div className="flex gap-2 justify-center">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              fileInputRef.current?.click();
                            }}
                            className="px-3 py-1.5 bg-teal-600 text-white text-sm rounded hover:bg-teal-700 transition">
                            Ganti Logo
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveLogo();
                            }}
                            className="px-3 py-1.5 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition">
                            Hapus
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center mx-auto">
                          <Upload className="w-6 h-6 text-gray-400" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-700">
                            Klik untuk upload logo
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            PNG, JPG, GIF (max 2MB)
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {data.label && data.label.toLowerCase().includes("logo") && (
                    <div className="mt-4 p-4 border border-gray-200 rounded-lg">
                      <label className="block text-[11px] font-semibold uppercase tracking-wider text-teal-600 mb-3">
                        TAMPILKAN LOGO DI NOTA CETAK
                      </label>
                      <div className="flex gap-3">
                        <button
                          type="button"
                          onClick={() => setPrintLogo(false)}
                          className={`flex-1 py-2 rounded-lg ring-1 text-sm font-semibold transition-colors ${
                            printLogo === false
                              ? "bg-teal-600 text-white ring-teal-600 shadow-sm shadow-teal-600/20"
                              : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"
                          }`}>
                          Non-Aktif
                        </button>
                        <button
                          type="button"
                          onClick={() => setPrintLogo(true)}
                          className={`flex-1 py-2 rounded-lg ring-1 text-sm font-semibold transition-colors ${
                            printLogo !== false
                              ? "bg-teal-600 text-white ring-teal-600 shadow-sm shadow-teal-600/20"
                              : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"
                          }`}>
                          Aktif
                        </button>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">
                        Pilih apakah logo ini akan dicetak pada nota kertas (print).
                      </p>
                    </div>
                  )}
                </div>
              ) : data.type === "toggle" ? (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setValue("Non-Aktif")}
                    className={`flex-1 py-2 rounded-lg ring-1 text-sm font-semibold transition-colors ${
                      value === "Non-Aktif"
                        ? "bg-teal-600 text-white ring-teal-600 shadow-sm shadow-teal-600/20"
                        : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"
                    }`}>
                    Non-Aktif
                  </button>
                  <button
                    type="button"
                    onClick={() => setValue("Aktif")}
                    className={`flex-1 py-2 rounded-lg ring-1 text-sm font-semibold transition-colors ${
                      value === "Aktif"
                        ? "bg-teal-600 text-white ring-teal-600 shadow-sm shadow-teal-600/20"
                        : "bg-white text-slate-500 ring-slate-200 hover:bg-slate-50"
                    }`}>
                    Aktif
                  </button>
                </div>
              ) : (
                <input
                  type="text"
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition">
              Batal
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 font-medium transition">
              Simpan
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
